import { UPDATE_DAYCARE_GROUP_DOWNLOAD_PARAMS } from '@/modules/API/daycareGroup/daycareGroupHandler'
import { useNotification } from '@/modules/context/NotificationContext'
import { DaycareGroup, DaycareGroupDownloadParams } from '@/types/DaycareGroup'
import { useMutation } from '@apollo/client'
import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from '../groupCustomization.module.scss'
import Checkbox from '@/components/_custom/Checkbox/Checkbox.tsx'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'

interface GroupDownloadParamsProps {
    group: DaycareGroup
    refetchGroups: () => void
    canWrite: boolean
}

const GroupDownloadParams: FunctionComponent<GroupDownloadParamsProps> = ({
    group,
    refetchGroups,
    canWrite
}: GroupDownloadParamsProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const defaultDownloadParams: DaycareGroupDownloadParams = { canDownloadDocuments: true, canDownloadImages: false }

    const [canDownloadImages, setCanDownloadImages] = useState<boolean>(
        group.downloadParams?.canDownloadImages ?? defaultDownloadParams.canDownloadImages
    )
    const [canDownloadDocuments, setCanDownloadDocuments] = useState<boolean>(
        group.downloadParams?.canDownloadDocuments ?? defaultDownloadParams.canDownloadDocuments
    )

    const downloadParams: DaycareGroupDownloadParams = {
        canDownloadImages,
        canDownloadDocuments
    }
    useEffect(() => {
        if (group.downloadParams) {
            setCanDownloadImages(group.downloadParams.canDownloadImages)
            setCanDownloadDocuments(group.downloadParams.canDownloadDocuments)
        }
    }, [group])

    const [updateGroupDownloadParams] = useMutation(UPDATE_DAYCARE_GROUP_DOWNLOAD_PARAMS, {
        variables: {
            groupId: group.id,
            params: downloadParams
        },
        onCompleted: () => {
            showNotificationSuccess(t('downloadParamsUpdated'))
            refetchGroups()
        },
        onError: () => {
            showNotificationError(t('errorUpdatingDownloadParams'))
        }
    })

    function handleChangeImageParam() {
        setCanDownloadImages(!canDownloadImages)
    }

    function handleChangeDocumentParam() {
        setCanDownloadDocuments(!canDownloadDocuments)
    }

    const hasChanges: boolean =
        canDownloadImages !== (group.downloadParams?.canDownloadImages ?? defaultDownloadParams.canDownloadImages) ||
        canDownloadDocuments !==
            (group.downloadParams?.canDownloadDocuments ?? defaultDownloadParams.canDownloadDocuments)

    return (
        <div className={styles.Container}>
            <div className={styles.Title}>
                <p>{t('downloadParams')}</p>
            </div>
            <Checkbox
                text={t('authorizeImagesDownloadForParents')}
                checked={canDownloadImages}
                onChange={handleChangeImageParam}
                disabled={!canWrite}
            />
            <Checkbox
                text={t('authorizeDocumentsDownloadForParents')}
                checked={canDownloadDocuments}
                onChange={handleChangeDocumentParam}
                disabled={!canWrite}
            />
            {canWrite && (
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'update'}
                        onClick={updateGroupDownloadParams}
                        className={styles.Button}
                        disabled={!hasChanges}
                    />
                </div>
            )}
        </div>
    )
}

export default GroupDownloadParams
